// ===========================================
// Teacher: my Meet & Greets (#255, enquiries Phase 4)
// ===========================================
// `/:slug/teacher/meet-and-greets`. The dashboard card shows only a handful; this is where a teacher
// finds one whose notes they still owe. Each row opens the notes page.

import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import PageHeader from '../../components/common/PageHeader';
import { NotesMissingChip } from '../../components/meetAndGreet/MeetAndGreetNotesList';
import { useSchoolTimezone, useTenant } from '../../contexts/TenantContext';
import { useTeacherMeetAndGreets } from '../../hooks/useMeetAndGreetNotes';
import { formatMeetingTime, teacherStatusLabel } from '../../utils/meetAndGreetNotes';

export default function TeacherMeetAndGreetsListPage() {
  const { schoolSlug } = useTenant();
  const timeZone = useSchoolTimezone();
  const [needsNotes, setNeedsNotes] = useState(false);
  const { data, isLoading, isError } = useTeacherMeetAndGreets(needsNotes);
  const rows = Array.isArray(data) ? data : [];

  return (
    <Box>
      <PageHeader title="My Meet & Greets" subtitle="Open one to see the family's details and record your notes" />
      <FormControlLabel
        control={<Switch checked={needsNotes} onChange={(e) => setNeedsNotes(e.target.checked)} />}
        label="Only those waiting for my notes"
        sx={{ mb: 1 }}
      />
      {isLoading ? (
        <Stack spacing={1}>
          <Skeleton variant="rectangular" height={56} />
          <Skeleton variant="rectangular" height={56} />
        </Stack>
      ) : isError ? (
        <Alert severity="error">Failed to load your Meet & Greets.</Alert>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">
          {needsNotes ? 'No notes are waiting for you.' : 'No Meet & Greets are assigned to you.'}
        </Typography>
      ) : (
        <Paper variant="outlined">
          <List disablePadding>
            {rows.map((row) => (
              <ListItemButton
                key={row.id}
                component={RouterLink}
                to={`/${schoolSlug}/teacher/meet-and-greets/${row.id}`}
                divider
              >
                <ListItemText
                  primary={`${row.studentFirstName} ${row.studentLastName}`.trim()}
                  secondary={formatMeetingTime(row.scheduledDateTime, timeZone)}
                  sx={{ minWidth: 0 }}
                />
                {row.status !== 'APPROVED' && (
                  <Chip label={teacherStatusLabel(row.status)} size="small" variant="outlined" sx={{ mr: 0.5 }} />
                )}
                {row.notesMissing && <NotesMissingChip />}
                {!row.notesMissing && row.completedAt && <Chip label="Completed" size="small" />}
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}
